// Dashboard widget + layout grammar (arc 101 slice 09, task #79). Adopted from
// tesote.ai's proven `WidgetSpec` (`packages/shared/src/dashboards.ts`), extended
// with `timeline` (a run-event feed) and `markdown` (agent-authored prose +
// mermaid diagrams).
//
// A `WidgetSpec` is a discriminated union over `kind`; adding a kind is a NEW
// branch, never a new conditional (OCP). Every DATA widget carries a read-only
// `WidgetQuery` + a `WidgetCapabilities` surface; `markdown` is the static
// exception — it renders authored content, so it holds no query and can express
// neither a read nor a write. The read-only invariant lives in `WidgetQuery`: a
// write-shaped widget is unrepresentable.
//
// Pure: imports only Zod + the read-query grammar. Specs are authored (agent /
// seed), stored as jsonb in `dashboard_widgets.spec`, and parsed on read.

import { z } from 'zod';
import { WidgetCapabilitiesSchema, WidgetQuerySchema } from './dashboard-query';

// ─── shared cell / series / column vocabulary ────────────────────────────────

/** How a client formats a raw cell value. `text` is the safe default; numeric
 * formats assume numeric data. Renderers may add local formats — the contract
 * validates the standard set. */
export const ColumnFormatSchema = z.enum([
  'text',
  'number',
  'currency',
  'date',
  'datetime',
  'boolean',
  'percent',
]);
export type ColumnFormat = z.infer<typeof ColumnFormatSchema>;

/** A result-set key referenced by a widget (column / axis / value key). */
const columnKey = z.string().min(1).max(128);

/** A table column — a result key, its header, and an optional format hint. */
export const TableColumnSchema = z
  .object({
    key: columnKey,
    label: z.string().min(1).max(128),
    format: ColumnFormatSchema.optional(),
  })
  .strict();
export type TableColumn = z.infer<typeof TableColumnSchema>;

/** A chart series — one line/bar/slice over a numeric result key. */
export const ChartSeriesSchema = z
  .object({
    key: columnKey,
    label: z.string().min(1).max(128),
  })
  .strict();

// ─── widget kinds ────────────────────────────────────────────────────────────

export const WidgetKind = {
  Table: 'table',
  Chart: 'chart',
  Stat: 'stat',
  List: 'list',
  Timeline: 'timeline',
  Markdown: 'markdown',
} as const;
export const WidgetKindSchema = z.enum(WidgetKind);
export type WidgetKind = z.infer<typeof WidgetKindSchema>;
/** Every widget kind, in render-catalog order. */
export const WIDGET_KINDS: readonly WidgetKind[] = WidgetKindSchema.options;

// Fields every DATA widget shares — the read query + the negotiable control
// surface + an optional title. `markdown` opts out (static content).
const dataWidgetBase = {
  /** The source-agnostic, read-only request resolved server-side. */
  query: WidgetQuerySchema,
  /** What the client may sort/filter/paginate without an agent turn. Omitted =
   * the zero-capability surface (fresh object per parse — never a shared default). */
  capabilities: WidgetCapabilitiesSchema.default(() => ({ sort: [], filter: [], paginate: false })),
  title: z.string().max(256).optional(),
};

/** Paginated, sortable, filterable rows. Absent `columns` = auto-map result keys. */
export const TableWidgetSchema = z
  .object({
    kind: z.literal(WidgetKind.Table),
    ...dataWidgetBase,
    // Keys unique: a key addresses the cell AND the rendered column, and the
    // dashboard's `DataTable` throws on a repeat inside a render.
    columns: z
      .array(TableColumnSchema)
      .superRefine((columns, ctx) => {
        const seen = new Set<string>();
        columns.forEach((c, i) => {
          if (seen.has(c.key)) {
            ctx.addIssue({
              code: 'custom',
              path: [i, 'key'],
              message: `duplicate column key "${c.key}" — each table column key must be unique`,
            });
          }
          seen.add(c.key);
        });
      })
      .optional(),
    page_size: z.number().int().positive().max(1000).default(25),
  })
  .strict();
export type TableWidget = z.infer<typeof TableWidgetSchema>;

/** A chart over a categorical / time-series result. `x` is the category axis;
 * `series` are the numeric columns to plot. */
export const ChartWidgetSchema = z
  .object({
    kind: z.literal(WidgetKind.Chart),
    ...dataWidgetBase,
    chart: z.enum(['bar', 'line', 'area', 'pie']),
    x: columnKey,
    series: z.array(ChartSeriesSchema).min(1),
  })
  .strict();
export type ChartWidget = z.infer<typeof ChartWidgetSchema>;

/** A single KPI number extracted from the first result row. */
export const StatWidgetSchema = z
  .object({
    kind: z.literal(WidgetKind.Stat),
    ...dataWidgetBase,
    value_key: columnKey,
    label: z.string().min(1).max(256),
    format: ColumnFormatSchema.default('number'),
    prefix: z.string().max(16).optional(),
    suffix: z.string().max(16).optional(),
  })
  .strict();
export type StatWidget = z.infer<typeof StatWidgetSchema>;

/** A feed/inbox list — a bold primary line + an optional muted secondary per row. */
export const ListWidgetSchema = z
  .object({
    kind: z.literal(WidgetKind.List),
    ...dataWidgetBase,
    primary_key: columnKey,
    secondary_key: columnKey.optional(),
    page_size: z.number().int().positive().max(200).default(25),
  })
  .strict();
export type ListWidget = z.infer<typeof ListWidgetSchema>;

/** A time-ordered event feed — the run-event timeline. `timestamp_key` orders it;
 * `title_key` is the headline, `description_key` the optional detail line. */
export const TimelineWidgetSchema = z
  .object({
    kind: z.literal(WidgetKind.Timeline),
    ...dataWidgetBase,
    timestamp_key: columnKey,
    title_key: columnKey,
    description_key: columnKey.optional(),
    page_size: z.number().int().positive().max(500).default(50),
  })
  .strict();
export type TimelineWidget = z.infer<typeof TimelineWidgetSchema>;

/** Static agent-authored markdown — prose + mermaid diagrams. The RENDERER
 * sanitizes (no raw HTML / script); the schema only bounds length. Carries NO
 * query (nothing to page), so it can never express a read or a write. */
export const MarkdownWidgetSchema = z
  .object({
    kind: z.literal(WidgetKind.Markdown),
    title: z.string().max(256).optional(),
    content: z.string().min(1).max(20_000),
  })
  .strict();
export type MarkdownWidget = z.infer<typeof MarkdownWidgetSchema>;

/** The widget grammar — discriminated on `kind`, stored as jsonb in
 * `dashboard_widgets.spec`, re-validated server-side before any read. */
export const WidgetSpecSchema = z.discriminatedUnion('kind', [
  TableWidgetSchema,
  ChartWidgetSchema,
  StatWidgetSchema,
  ListWidgetSchema,
  TimelineWidgetSchema,
  MarkdownWidgetSchema,
]);
export type WidgetSpec = z.infer<typeof WidgetSpecSchema>;

// ─── dashboard scope ─────────────────────────────────────────────────────────

/** Who a dashboard is for. `org` = shared, member-visible; `user` = personal,
 * cross-org; `custom` = JSON-defined via API/MCP. Built-ins ship as `org`/`user`
 * seeds; "customize" = clone to a `custom` copy. */
export const DashboardScope = {
  Org: 'org',
  User: 'user',
  Custom: 'custom',
} as const;
export const DashboardScopeSchema = z.enum(DashboardScope);
export type DashboardScope = z.infer<typeof DashboardScopeSchema>;
export const DASHBOARD_SCOPES: readonly DashboardScope[] = DashboardScopeSchema.options;

// ─── layout — the 12-col grid ────────────────────────────────────────────────

/** Total grid width — spans are `1..DASHBOARD_GRID_COLUMNS`. */
export const DASHBOARD_GRID_COLUMNS = 12;

/** One placed widget — a reference into `dashboard_widgets` + its column span.
 * Content is NEVER inlined here, so the same widget can appear on many dashboards;
 * deleting a ref does not delete the widget row (the service owns that). */
export const LayoutWidgetRefSchema = z
  .object({
    widgetId: z.string().min(1),
    /** Column span in the 12-col grid. */
    span: z.number().int().min(1).max(DASHBOARD_GRID_COLUMNS),
  })
  .strict();

/** The dashboard layout: an ordered flow of widget refs packed left-to-right into
 * the 12-col grid, wrapping to a new visual row when the running span sum would
 * exceed 12. Presentational only — reordering never touches widget content. */
export const DashboardLayoutSchema = z
  .object({
    rows: z.array(LayoutWidgetRefSchema).min(1),
  })
  .strict();
export type DashboardLayout = z.infer<typeof DashboardLayoutSchema>;
